import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IMAGE_ID_URL } from '../../config'
import { addTarget, checkIfColorIsBeingUsed, editColorFromDetails, removeStepEmblemData, saveEmblemData, wizardStore } from '../../stores/wizard'
import { useSnapshot } from 'valtio'
import SpecialInstructionsTextarea from '../Wizard/commonComps/SpecialInstructionsTextarea'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import AcceptedFileTypesModal from './AcceptedFileTypesModal'
import { Steps } from '../../constants'
import { getContrastColor } from '../../utils/getContrastColor'
import { CircleIndicator } from './commonComps/CircleColor'
import ColorTooltip from './commonComps/ColorTooltip'
import FileDetailsViewer from './commonComps/FileDetailsViewer'
import { Circle, CircleData, RGBAColor, Step } from '../../types/wizard'
import useOutsideClick from '../../hooks/useOutsideClick'
import DesignGuide from './commonComps/EmblemLeather/DesignGuide'
import { RGBAToHexA } from '../../utils/rgbToHex'
import { isHexColor } from '../../utils/isHexColor'
import { uploadFile } from '../../api/upload'
import ColorWarningModal from './ColorWarningModal'
import { colorWarningStore } from '../../stores/colorWarningStore'

type FileDetails = {
  name: string;
  type: string;
  size: string;
  dimensions?: { width: number; height: number };
};

type DesignProps = {
  step: Step;
};

export const Design: React.FC<DesignProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const { editedCircleColor, hoveredColorId } = wizSnap;
  const stepColorId = step.id === Steps.DesignType ? Steps.ThreadColors : Steps.HolographicColors;
  const [message, setMessage] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loadingImage, setLoadingImage] = useState(false);
  const [fileDetails, setFileDetails] = useState<FileDetails | null>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const elementRef = useRef<HTMLImageElement>(null);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const [color, setColor] = useState<string | null>(null);
  const [rgbaColor, setRgbaColor] = useState<RGBAColor>({ r: 0, g: 0, b: 0, a: 1 });
  const curretTarget = wizSnap?.target[stepColorId]?.circle;
  const [target, setTarget] = useState<Circle | null>(curretTarget);
  const [savedTargets, setSavedTargets] = useState<CircleData[]>([]);
  const [saved3dIndications, setSaved3dIndications] = useState<CircleData[]>([]);
  const [selectedCircle, setSelectedCircle] = useState<{ x: number; y: number; color: string, isEditing?: boolean } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const contrastColor = useMemo(() => color ? getContrastColor(color) : "#fff", [color]);

  const idOfUserFileUploaded = useMemo(() => {
    const emblemData = wizSnap.emblemData[step.id];
    return emblemData?.imageId || null;
  }, [wizSnap.emblemData, step.id]);

  const title = useMemo(() => {
    const holographicUploadStep = wizSnap.steps?.find(s => s.id === Steps.HolographicUpload);

    if (holographicUploadStep) {
      if (step.id === Steps.DesignType) {
        return "UPLOAD IMAGE (1 OF 2)";
      } else if (step.id === Steps.HolographicUpload) {
        return holographicUploadStep.title || "UPLOAD IMAGE";
      }
    }

    return "UPLOAD IMAGE";
  }, [wizSnap.steps, step.id]);

  useEffect(() => {
    // Get saved colors
    if (wizSnap?.emblemData?.[stepColorId])
      setSavedTargets(wizSnap.emblemData[stepColorId]?.items);

    // Get saved Emblem 3D indications
    if (wizSnap?.emblemData?.[Steps.Emblem3d])
      setSaved3dIndications(wizSnap.emblemData[Steps.Emblem3d]?.items)
  }, [wizSnap]);

  useEffect(() => {
    if (elementRef.current && imageLoaded) {
      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d', { willReadFrequently: true });

      if (ctx) {
        const image = new Image();
        image.crossOrigin = 'Anonymous';
        image.src = elementRef.current.src;

        image.onload = () => {
          tempCanvas.width = elementRef.current!.width;
          tempCanvas.height = elementRef.current!.height;
          ctx.drawImage(image, 0, 0, tempCanvas.width, tempCanvas.height);
          setCanvas(tempCanvas);
        };
      }
    }
  }, [elementRef.current?.src, imageLoaded]);

  useEffect(() => {
    if (selectedCircle) {
      const { x, y } = selectedCircle;
      const hexValue =
        isHexColor(selectedCircle.color) ? selectedCircle.color : RGBAToHexA(selectedCircle.color);

      handleSelection(hexValue, rgbaColor, { x, y });
    }
  }, [selectedCircle]);

  useEffect(() => {
    if (editedCircleColor?.circle && editedCircleColor.color) {
      executeEdit(editedCircleColor);
    }
  }, [editedCircleColor]);

  const handleColorUpdate = (rgba: { r: number; g: number; b: number; a: number }, hex: string) => {
    setRgbaColor(rgba);
    setColor(hex);
  };

  const getPixelColor = (
    x: number,
    y: number,
    handleColorUpdate?: (rgba: { r: number; g: number; b: number; a: number }, hex: string) => void
  ): Promise<string> => {
    return new Promise((resolve) => {
      if (!canvas) {
        resolve('');
        return;
      }

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        resolve('');
        return;
      }

      const pixelData = ctx.getImageData(x, y, 1, 1).data;
      const r = pixelData[0];
      const g = pixelData[1];
      const b = pixelData[2];
      const a = pixelData[3];

      const hexColor = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
      const rgba = { r, g, b, a };

      // Update the state if the callback is provided
      if (handleColorUpdate) {
        handleColorUpdate(rgba, hexColor);
      }

      resolve(hexColor);
    });
  };

  const handleClick = async (event: React.MouseEvent) => {
    if (elementRef.current) {
      //@ts-ignore
      const rect = elementRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const color = await getPixelColor(x, y, handleColorUpdate);
      const cColor = contrastColor;

      setTarget({ id: Date.now(), x, y, color, contrastColor: cColor });
    }
  };

  const onUploadFileBtnPress = async (file: File) => {
    if (!file) {
      setMessage('Please select a file.')
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('Model', JSON.stringify({
      config: 'Stahls',
      ident: {
        superuser: false,
        sessionid: 'dc852e74-d3f0-42e8-95eb-4f3dbc6c16cc'
      }
    }))

    const result = await uploadFile(file);

    if (result.success && result.imageId) {
      setMessage(`File uploaded successfully!`);
      setLoadingImage(false);
      saveEmblemData(step.id, {
        imageId: result.imageId,
        imageSrc: IMAGE_ID_URL.replace("imageId", result.imageId)
      });
    } else {
      setMessage(`Core reported failure, file upload was not successful.`);
    }
  }

  const getFileData = (file: File) => {
    const fileSizeKB = (file.size / 1024).toFixed(1); // Size in KB
    const details: FileDetails = {
      name: file.name,
      type: file.type,
      size: `${fileSizeKB} KB`,
    };

    // if it's an image, get dimensions
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setFileDetails({
            ...details,
            dimensions: { width: img.width, height: img.height },
          });
        };
        if (e.target?.result) {
          img.src = e.target.result as string;
        }
      };
      reader.readAsDataURL(file);
    } else {
      setFileDetails(details);
    }
  }

  const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Get File info
      getFileData(file);

      setSelectedFile(file)
      setMessage('') // Clear message when new file is selected
      setLoadingImage(true)
      await onUploadFileBtnPress(file)
    }
  }

  const onDrop = async (acceptedFiles: File[]) => {
    // Handle the files from drop
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0])
      setMessage('') // Clear message when new file is selected
      setLoadingImage(true)
      await onUploadFileBtnPress(acceptedFiles[0])
    }
  }

  const handleRemoveImage = () => {
    setMessage("")
    setSelectedFile(null)
    removeStepEmblemData(step.id)
    addTarget(stepColorId, {} as Circle);
    setSelectedCircle(null);
    setTarget(null);

    // Remove saved colors
    saveEmblemData(stepColorId, { items: [] })
  }

  const handleCircleClick = (position: { x: number; y: number }, color: string, isEditing = false) => {
    setSelectedCircle({ ...position, color, isEditing });
  };

  // Tooltip select options (pick up color or search by code)
  const handleSelection = (
    color: string,
    rgbaValue: RGBAColor,
    position: { x: number; y: number }) => {
    if (!selectedCircle) return;

    addTarget(stepColorId, {
      id: Date.now(),
      x: position.x,
      y: position.y,
      color,
      rgba: rgbaValue
    });
  };

  const saveData = (colorToRemove: CircleData) => {
    const { stepId, circle } = colorToRemove;
    const items = wizSnap.emblemData[stepId]?.items ?? [];
    //@ts-ignore
    const filteredItems = items.filter(item => item.circle?.id !== circle.id);
    saveEmblemData(stepId, { items: filteredItems });
    setSelectedCircle(null);
  }

  const handleRemoveColor = (circleData: CircleData) => {
    if (checkIfColorIsBeingUsed(circleData)) {
      colorWarningStore.openModal(circleData, false, () => saveData(circleData));
    } else {
      saveData(circleData);
    }
  };

  const handleEditColor = (circleData: CircleData) => {
    if (checkIfColorIsBeingUsed(circleData)) {
      colorWarningStore.openModal(circleData, true, () => {
        setTimeout(() => {
          executeEdit(circleData);
        }, 50);
      });
    } else {
      executeEdit(circleData);
    }
  };

  const executeEdit = (circleData: CircleData) => {
    const { stepId, circle, color } = circleData;
    const currentItems = wizSnap.emblemData[stepId]?.items || [];
    const item = currentItems.find((c: CircleData) => c.circle?.id === circle?.id);

    if (item?.circle && color) {
      const { x, y } = item.circle;
      handleCircleClick({ x, y }, color, true);
    }
  };

  useOutsideClick([imgContainerRef, tooltipRef], () => {
    setSelectedCircle(null);
    setTarget(null);
    editColorFromDetails(null);
  });

  return (
    <>
      <div className='flex flex-col'>
        {wizSnap.isEmblemLeather && <DesignGuide />}
        <div className="w-full mt-5 flex flex-row gap-2 pb-4">
          <label className="block uppercase font-bold mb-2.5">{title}</label>
          {step.id === Steps.DesignType && <span className='cursor-pointer'
            onClick={() => setModalOpen(true)}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>}
        </div>
        {idOfUserFileUploaded ? (
          <div className='relative flex justify-center items-center img-wrapper'
            ref={imgContainerRef}>
            <img className='w-full h-full bg-cover cursor-crosshair'
              ref={elementRef}
              onClick={handleClick}
              onLoad={() => setImageLoaded(true)}
              src={IMAGE_ID_URL.replace("imageId", idOfUserFileUploaded)} />

            {/* Available for Steps.ThreadColors and Steps.HolographicColors */}
            {
              !wizSnap.isEmblemLeather ? (
                <>
                  {target && (
                    <CircleIndicator
                      circle={target}
                      isTarget={true}
                      key={target.id}
                      color={target.color}
                      contrastColor={target?.contrastColor ? target?.contrastColor : 'black'}
                      onClick={() => handleCircleClick({ x: target.x, y: target.y }, target.color)}
                    />
                  )}

                  {/* Show SAVED COLORS as circles */}
                  {savedTargets?.map((target: any) => {
                    const circle = target?.circle;

                    if (circle)
                      return (
                        <CircleIndicator
                          circle={circle}
                          indications={saved3dIndications}
                          isTarget={false}
                          key={circle.id}
                          color={target.color}
                          hoveredColorId={hoveredColorId}
                          contrastColor={getContrastColor(target.color)}
                          onClick={() => handleCircleClick({ x: circle.x, y: circle.y }, target.color)}
                        />
                      );
                  })}

                  {selectedCircle && (
                    <ColorTooltip
                      ref={tooltipRef}
                      isEditing={selectedCircle.isEditing ?? false}
                      stepColorId={stepColorId}
                      targets={!selectedCircle.isEditing ? savedTargets : []}
                      indications={saved3dIndications}
                      containerRef={imgContainerRef}
                      position={selectedCircle}
                      onRemove={handleRemoveColor}
                      onEdit={handleEditColor}
                    />
                  )}
                </>
              ) : null
            }
          </div>
        ) : (
          <div
            style={{ border: '2px dashed #a7acaf' }}
            className="flex flex-col justify-center items-center h-72 cursor-pointer"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              onDrop(Array.from(e.dataTransfer.files))
            }}>
            {loadingImage ? <span className="loading loading-dots loading-lg"></span> : (
              <>
                <input type="file" id={`fileUpload-${step.id}`} className="hidden" onChange={onFileInputChange} />
                <div className="text-center">
                  <button className="btn-file bg-black text-white underline rounded" onClick={() => document.getElementById(`fileUpload-${step.id}`)?.click()}>Select a file from computer</button>
                  <p className="my-2 font-bold">or</p>
                  <p className='text-black text-xl capitalize font-bold'>Drag & Drop Here</p>
                </div>
                <p className="mt-4">{message}</p>
              </>
            )}
          </div>
        )}
        {!idOfUserFileUploaded
          ?
          <div className='flex items-center justify-between relative bottom-8'>
            <p className='text-slate-400 text-sm pl-4'>Maximum file size is 25 MB</p>
          </div>
          :
          <div className='text-black w-full text-right'>
            <button
              onClick={handleRemoveImage}
              className='outline-none hover:outline-none focus:outline-none border-0 bg-inherit underline text-sm px-0 py-4'>
              Remove
            </button>
          </div>
        }
        {selectedFile ? (
          <div className='flex items-center justify-center relative bottom-4'>
            <FileDetailsViewer fileDetails={fileDetails} />
          </div>
        ) : null}
        <SpecialInstructionsTextarea />
      </div>
      {/* Accepted Fily Types Modal */}
      <AcceptedFileTypesModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />

      {/* Modal Warning Message */}
      <ColorWarningModal />
    </>
  )
}
